import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  DEPLOYMENT_FRAMEWORKS,
  DeploymentFramework,
  DeploymentFrameworkDef,
} from '@/mock/gpu-instance';
import { Zap, Server, Layers } from 'lucide-react';

interface FrameworkSelectorProps {
  selectedFramework: DeploymentFramework;
  onFrameworkChange: (framework: DeploymentFramework) => void;
  disabled?: boolean;
}

const getFrameworkIcon = (icon: DeploymentFrameworkDef['icon']) => {
  switch (icon) {
    case 'Zap':
      return <Zap className="h-5 w-5 text-yellow-500" />;
    case 'Server':
      return <Server className="h-5 w-5 text-green-500" />;
    default:
      return null;
  }
};

const FrameworkSelector = ({
  selectedFramework,
  onFrameworkChange,
  disabled = false,
}: FrameworkSelectorProps) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Label className="text-base font-medium flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Deployment Framework
          </Label>
          <p className="text-sm text-muted-foreground">
            Select the inference framework for your models
          </p>
        </div>
        <Badge variant="secondary">Demo</Badge>
      </div>

      <RadioGroup
        value={selectedFramework}
        onValueChange={(v) => onFrameworkChange(v as DeploymentFramework)}
        disabled={disabled}
        className="grid grid-cols-2 gap-3"
      >
        {DEPLOYMENT_FRAMEWORKS.map((framework) => (
          <Card
            key={framework.id}
            className={`cursor-pointer transition-colors ${
              selectedFramework === framework.id
                ? 'border-primary bg-primary/5'
                : 'hover:border-muted-foreground/50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !disabled && onFrameworkChange(framework.id)}
          >
            <CardContent className="flex flex-col items-center gap-2 p-4">
              <RadioGroupItem
                value={framework.id}
                id={`framework-${framework.id}`}
                disabled={disabled}
                className="sr-only"
              />
              <div className="flex items-center gap-2">
                {getFrameworkIcon(framework.icon)}
                <label
                  htmlFor={`framework-${framework.id}`}
                  className="font-medium cursor-pointer"
                >
                  {framework.name}
                </label>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {framework.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </RadioGroup>
    </div>
  );
};

export default FrameworkSelector;
